import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { DEPOSIT_ACCOUNT_CHANGED_EVENT } from '@/supabase/deposit/service/depositAccountService';
import * as depositService from '@/supabase/deposit/service/depositService';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { isPaidPlanId } from '@/lib/planAccess';

import { useDepositAccountStatus } from './useDepositAccountStatus';

// nav '입금매칭' 확인 대기 카운트(증분2 D3~D6).
// - linked 셀러만 조회(미연동/판정전은 쿼리 자체 없음 = 배지 없음.
//   스펙 D4의 'NEW 유지'는 네비 간소화(2026-07-30)로 폐기).
// - deposit_match realtime(전용 'deposit-badge' 채널) + 400ms 디바운스(useDepositIncoming 패턴).
// - 실패/미조회는 count=null → 배지 숨김(fail-quiet, 스펙 §5).
export function useDepositActionCount() {
  const { user } = useAuth();
  const { subscription } = useSubscription();
  // 무료 플랜(및 구독 미확정)은 카운트 쿼리·realtime 없음 = 배지 없음.
  const userId = isPaidPlanId(subscription?.subscription_plan_id) ? (user?.id ?? null) : null;
  const location = useLocation();
  const { status: accountStatus, refresh: refreshAccountStatus } = useDepositAccountStatus();
  const linked = accountStatus === 'linked';

  // 미연동 셀러가 /deposits에서 계좌를 연동해도 nav의 계정상태 인스턴스는 마운트 시점
  // 스냅샷이라 카운트 배지가 뜨지 못한 채 잔존한다(codexloop R1 #1). 미연동 상태에서만 라우트 전환마다 재판정 —
  // 연동은 /deposits에서 일어나므로 다음 내비게이션에 자연 치유되고, linked가 되면 이
  // effect는 더 이상 쿼리를 내지 않는다(미연동 셀러당 내비게이션 1회 경량 조회가 상한).
  useEffect(() => {
    if (accountStatus === 'unlinked' || accountStatus === 'in_progress') {
      void refreshAccountStatus();
    }
    // accountStatus dep는 안전: refresh가 같은 값을 다시 set하면 React가 bail-out(재렌더 없음)
    // → 재발화 루프 불가. 상태가 실제로 바뀐 경우(unlinked→in_progress)만 1회 추가 재판정.
  }, [location.pathname, accountStatus, refreshAccountStatus]);

  // 같은 탭 계좌 mutation(연동/해제/활성 토글) → 즉시 재판정(codexloop R2 #1).
  // realtime은 DELETE 이벤트에 채널 필터가 안 걸려 hard delete를 놓치므로 DOM 이벤트가 주 채널.
  useEffect(() => {
    const onAccountMutated = () => { void refreshAccountStatus(); };
    window.addEventListener(DEPOSIT_ACCOUNT_CHANGED_EVENT, onAccountMutated);
    return () => window.removeEventListener(DEPOSIT_ACCOUNT_CHANGED_EVENT, onAccountMutated);
  }, [refreshAccountStatus]);
  const [count, setCount] = useState<number | null>(null);
  const reqSeqRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!userId || !linked) return;
    const seq = ++reqSeqRef.current;
    const next = await depositService.countActionNeeded(userId);
    if (seq !== reqSeqRef.current) return;   // 셀러 전환/언마운트 뒤 늦은 응답 무시
    setCount(next);
  }, [userId, linked]);

  useEffect(() => {
    if (!userId || !linked) {
      reqSeqRef.current++;
      setCount(null);
      return;
    }
    void refresh();
    let debounce: ReturnType<typeof setTimeout> | null = null;
    const onChange = () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => { void refresh(); }, 400);
    };
    // SUBSCRIBED(첫 구독+재연결) 시 즉시 재조회(codexloop R1 #2): 스냅샷과 구독 시작 사이,
    // 그리고 끊김 동안 놓친 변경을 보정한다. 첫 구독의 중복 refresh는 seq 가드로 무해.
    const onStatus = (status: string) => {
      if (status === 'SUBSCRIBED') void refresh();
    };
    // 계좌 변경(연동 해제 등) → 계정상태 재판정(codexloop R2 #1). linked가 풀리면 이 effect가
    // 재실행돼 count를 null로 리셋하고 구독을 정리한다(배지 없음 상태로 복귀).
    const onAccountChange = () => { void refreshAccountStatus(); };
    const unsubscribe = depositService.subscribeToDepositBadge(
      userId, onChange, onAccountChange, onStatus);
    return () => {
      if (debounce) clearTimeout(debounce);
      unsubscribe();
    };
  }, [userId, linked, refresh, refreshAccountStatus]);

  // 언마운트 후 늦게 도착하는 응답이 state를 건드리지 않게(stale-write 방지) — useDepositIncoming 선례.
  useEffect(() => () => {
    reqSeqRef.current++;
  }, []);

  return { accountStatus, count };
}
